import _ from 'lodash';
import { parse } from 'graphql';
import objectMapper from 'object-mapper';
import { getContentType, getContentTypeByConfigArg } from './content';

const prepareFields = fields => Object.keys(fields).map(fieldName => ({ ...fields[fieldName], field_name: fieldName }));

const filterFields = (fields, property, value) => prepareFields(fields).filter(field => field[property] === value).map(field => field.field_name);

const rootFields = ['uuid', 'path', 'workspace', 'status', 'online', 'startPublicationDate', 'endPublicationDate', 'version', 'previousETag', 'eTag', '__typename', 'lastUpdateUserEmail', 'lastUpdateUserName', 'lastUpdateTime', 'createUserEmail', 'createUserName', 'createTime', 'publishedAt', 'wtype'];

const buildContentFields = (fields, contentTypes, deepFields = false) => {
  let queryFields = '';

  const preparedFields = prepareFields(fields);

  preparedFields.forEach(fieldConfig => {
    if (rootFields.includes(fieldConfig.field_name)) {
      return;
    }

    queryFields += ` ${fieldConfig.field_name}`;

    switch (fieldConfig.viewType) {
      case 'Cloudinary': {
        if (fieldConfig?.type === 'ImageCloudinary') {
          const cloudinarySubfields = [];
          if (fieldConfig?.metadata) {
            cloudinarySubfields.push('metadata');
          }
          if (fieldConfig?.caption) {
            cloudinarySubfields.push('caption');
          }

          queryFields += `{ url ${cloudinarySubfields.join(' ')} }`;
        }
        break;
      }
      case 'Entity': {
        const entityContentType = getContentTypeByConfigArg('type', fieldConfig.type, contentTypes);
        if (!entityContentType) {
          throw new Error(`Entity type "${fieldConfig.type}" not found!`);
        }
        let entityContent = entityContentType.configs.wtype ? 'uuid wtype __typename' : '';

        if (entityContentType.fields.uuid) {
          entityContent += ' uuid';
        }

        queryFields += ` { ${entityContent} ${buildContentFields(entityContentType.fields, contentTypes)} }`;
        break;
      }
      case 'EntitySelect': {
        const entitySelectContentType = getContentTypeByConfigArg('wtype', fieldConfig.selectWtype, contentTypes);
        if (!entitySelectContentType) {
          throw new Error(`EntitySelect wtype "${fieldConfig.selectWtype}" not found!`);
        }
        queryFields += ` { uuid wtype ${buildContentFields(entitySelectContentType.fields, contentTypes)} } `;
      } break;
      case 'PickerExternal': {
        const entityContentType = getContentTypeByConfigArg('type', fieldConfig.type, contentTypes);
        if (!entityContentType) {
          throw new Error(`EntitySelect wtype "${fieldConfig.selectWtype}" not found!`);
        }

        queryFields += ` { ${buildContentFields(entityContentType.fields, contentTypes)} }`;
        break;
      }
      case 'Picker': {
        const pickerContentType = getContentTypeByConfigArg('wtype', fieldConfig.contentWtype, contentTypes);

        if (!pickerContentType) {
          throw new Error('Picker wtype not found!');
        }

        // If is a variation
        let queryParent = '';
        if (deepFields) {
          if (pickerContentType.configs.nestedField) {
            queryParent += `parent { uuid wtype ${buildContentFields(pickerContentType.fields, contentTypes)} __typename }   }`;
          }
          // WHEN DEEP INFINIT CYCLE
          queryFields += ` { uuid wtype ${buildContentFields(pickerContentType.fields, contentTypes)} ${queryParent} __typename  }`;
        } else {
          if (pickerContentType.configs.nestedField) {
            queryParent = ' parent { uuid wtype __typename } ';
          }
          queryFields += ` { uuid wtype ${filterFields(pickerContentType.fields, 'showInList', true).join(' ')} ${queryParent} __typename }`;
        }
        break;
      }
      case 'CloudinarySEO': {
        const entityContentType = getContentTypeByConfigArg('type', fieldConfig.type, contentTypes);
        if (!entityContentType) {
          throw new Error(`EntitySelect wtype "${fieldConfig.selectWtype}" not found!`);
        }

        queryFields += ` { ${buildContentFields(entityContentType.fields, contentTypes)} }`;
        break;
      }
      default:
    }

    switch (fieldConfig.viewTypeCustom) {
      case 'PriceTab': {
        const contentType = getContentTypeByConfigArg('type', fieldConfig.type, contentTypes);
        queryFields += ` { ${buildContentFields(contentType.fields, contentTypes, true)} __typename }`;
        break;
      }
      case 'CSVTable': {
        const csvTableContentType = getContentTypeByConfigArg('type', fieldConfig.type, contentTypes);

        if (!csvTableContentType) {
          throw new Error('CSVTable wtype not found!');
        }

        queryFields += `{ uuid ${buildContentFields(csvTableContentType.fields, contentTypes)} } `;
        break;
      }

      case 'HoranetTariffs': {
        const horanetTariffContentType = getContentTypeByConfigArg('type', fieldConfig.type, contentTypes);

        if (!horanetTariffContentType) {
          throw new Error('CSVTable wtype not found!');
        }

        queryFields += `{${buildContentFields(horanetTariffContentType.fields, contentTypes)} } `;
        break;
      }

      default: {
        break;
      }
    }
  });

  return `${queryFields}`;
};

const buildContentQuery = (contentTypes, contentType) => {
  const endPoint = `${contentType.configs.single}`;
  console.log('buildContentQuery', contentType.fields);
  const fields = buildContentFields(contentType.fields, contentTypes);

  return `query ${endPoint} ($uuid: ID) { ${endPoint} (uuid: $uuid) { ${rootFields.join(' ')} ${fields}  } }`;
};

const buildContentMutation = (contentTypes, contentType, mutationType = 'create') => {
  const { wtype, inputType, pageBuilder, emailBuilder } = contentType.configs;

  const endPoint = `${contentType.configs[mutationType]}`;

  let extraArgsFunction = '';
  let extraArgsEndpoint = '';
  if (pageBuilder || emailBuilder) {
    extraArgsFunction = '$systemInformation: JSON';
    extraArgsEndpoint = 'systemInformation: $systemInformation';
  }

  const fields = buildContentFields(contentType.fields, contentTypes);

  return `mutation ${endPoint} ($input: ${inputType} ${extraArgsFunction}) { ${endPoint} (input: $input ${extraArgsEndpoint}) { ${rootFields.join(' ')} ${fields} } }`;
};

/*
 * Generates an array of string paths for all nested properties in an object or array.
*/
function paths (obj, parentKey) {
  let result;
  if (_.isArray(obj)) {
    let idx = 0;
    result = _.flatMap(obj, obj => paths(obj, `${parentKey || ''}[${idx++}]`));
  } else if (_.isPlainObject(obj)) {
    result = _.flatMap(_.keys(obj), key => _.map(paths(obj[key], key), subkey => (parentKey ? `${parentKey}.` : '') + subkey));
  } else {
    result = [];
  }
  return _.concat(result, parentKey || []);
}

const getMappingFromASTSelection = (astSelection, contentType, rawContentTypes) => {
  const queryPaths = {};

  astSelection.selectionSet.selections.forEach(auxSelection => {
    // Check if the selection has sub-fields (nested selection set)
    if (auxSelection.selectionSet) {
      const attributeName = auxSelection.alias ? auxSelection.alias.value : auxSelection.name.value;

      const field = contentType.fields[auxSelection.name.value];

      let auxContentType = contentType;

      if (auxSelection.name.value === 'parent') {
        // Get parent content type
        auxContentType = Object.values(rawContentTypes).find(a => a.configs.wtype === contentType.configs.parent_wtype);
      }

      if (field && field.contentWtype) {
        // Get contentType
        auxContentType = Object.values(rawContentTypes).find(a => a.configs.wtype === field.contentWtype);
      }
      if (field && field.viewType === 'Entity') {
        // Get entity
        auxContentType = Object.values(rawContentTypes).find(a => a.configs.type === field.type);
      }

      const objectFieldMapping = getMappingFromASTSelection(auxSelection, auxContentType, rawContentTypes);

      for (const [key, value] of Object.entries(objectFieldMapping)) {
        if (field && field.multivalued) {
          queryPaths[`${auxSelection.name.value}[].${key}`] = `${attributeName}[].${value}`;
        } else {
          queryPaths[`${auxSelection.name.value}.${key}`] = `${attributeName}.${value}`;
        }
      }
    } else {
      const fieldKey = auxSelection.alias?.value ? auxSelection.alias.value : auxSelection.name.value;

      queryPaths[auxSelection.name.value] = fieldKey;
    }
  });

  return queryPaths;
};

const generateContentQueryMapping = (query, contentType, rawContentTypes) => {
  try {
    // get query GraphQl AST object
    const queryParsed = parse(query);

    const queryASTDefinition = queryParsed.definitions.find(definition => definition.operation === 'query');

    return getMappingFromASTSelection(queryASTDefinition.selectionSet.selections[0], contentType, rawContentTypes);
  } catch (error) {
    console.log('[ERROR] generateContentQueryMapping: ', error);
    return null;
  }
};

/** ****************************
  PICKERS
**************************** */

const createRelatedContentQueries = (relations, contentTypes) => {
  const relationContentQueries = [];
  relations.forEach((relation, index) => {
    const relationContentType = getContentType(relation.wtype, contentTypes);

    // Send content type
    if (relationContentType) {
      let queryRelation = buildContentFields(relationContentType.fields, contentTypes, true);

      if (relationContentType.configs.nestedField) {
        const parentContentType = getContentType(relationContentType.configs.parent_wtype, contentTypes);
        queryRelation += ` parent { uuid wtype ${buildContentFields(parentContentType.fields, contentTypes, true)} __typename } `;
      }

      relationContentQueries.push(`PICKER_NUMBER_${index}: ${relationContentType.configs.single} (uuid:"${relation.uuid}") { uuid ${queryRelation} wtype }`);
    }
  });

  return relationContentQueries;
};

/**
 * @param {string[]} fields - Array of field strings. Example: [ "uuid", "name", "parent.url", "parent.name" ]
 */
function fieldsArrayToFieldsString (fields) {
  let fieldsString = '';

  const groupedFields = fields.reduce((acc, item) => {
    // Check if the string has a dot; if not, add it to the "root" group
    const hasDot = item.includes('.');
    const prefix = hasDot ? item.split('.')[0] : 'root';

    // Initialize the array for this prefix if it doesn't exist
    if (!acc[prefix]) {
      acc[prefix] = [];
    }

    // Remove the prefix and add the rest of the string to the array
    acc[prefix].push(hasDot ? item.slice(item.indexOf('.') + 1) : item);

    return acc;
  }, {});

  Object.keys(groupedFields).forEach(fieldGroupKey => {
    if (fieldGroupKey === 'root') {
      fieldsString = `${fieldsString} ${groupedFields[fieldGroupKey].join(' ')}`;
    } else {
      let subAttributes = fieldsArrayToFieldsString(groupedFields[fieldGroupKey]);

      if (fieldGroupKey === 'parent') {
        subAttributes = `${subAttributes} __typename`;
      }

      fieldsString = `${fieldsString} ${fieldGroupKey} { ${subAttributes} }`;
    }
  });

  return fieldsString;
}

const createRelatedContentQueriesFromMapping = (relations, previewContentTypeQueryMapping, contentTypes) => {
  const relationContentQueries = [];

  relations.forEach((relation, index) => {
    const pathMapping = JSON.parse(JSON.stringify(previewContentTypeQueryMapping));

    // Remove array "[]" and .uuid
    const pathPrefix = relation.path.replace(/\[\d+\]/g, '[]').replace('.uuid', '');

    let relationFields = Object.keys(pathMapping).filter(value => value.startsWith(`${pathPrefix}.`));

    relationFields = relationFields.map(path => path.replace(`${pathPrefix}.`, '').replace('[]', ''));

    const contentFields = fieldsArrayToFieldsString(relationFields);

    if (contentFields) {
      const relationContentType = getContentType(relation.wtype, contentTypes);

      relationContentQueries.push(`PICKER_NUMBER_${index}: ${relationContentType.configs.single} (uuid:"${relation.uuid}") { uuid wtype ${contentFields} }`);
    }
  });

  return relationContentQueries;
};

/** ****************************************** */

const remapContentToQueryFields = (dataNewSchema, data) => objectMapper(data, dataNewSchema);

const prepareObject = (value, multivalued) => {
  if (multivalued) { //  [{ uuid: "XXX", ... }] to ["XXX"]
    return value && value.length ? value.filter(v => v !== null).map(({ uuid }) => uuid) : [];
  } // { uuid: "XXX", ... } to "XXX"

  return value ? value.uuid : null;
};

const prepareImageCloudinary = (value, multivalued) => {
  if (multivalued) {
    return value && value.length ? value.map(({ url, caption }) => ({ url, caption })) : [];
  }
  return value ? { url: value.url, caption: value.caption } : null;
};

const variablesInputSetup = (contentType, contentTypes, content, status, path, workspace) => {
  const data = {};

  if (status) {
    data.status = status;
  }

  if (path) {
    data.path = path;
  }

  if (workspace) {
    data.workspace = workspace;
  }

  const preparedFields = prepareFields(contentType.fields);
  preparedFields.forEach(fieldConfig => {
    const fieldName = fieldConfig.field_name;

    // FIELDS TO IGNORE
    if (['workspace', 'path', 'status', 'wtype', 'previousETag', 'eTag', 'lastUpdateUserEmail', 'lastUpdateUserName', 'lastUpdateTime', 'createUserEmail', 'createUserName', 'createTime', 'publishedAt'].includes(fieldConfig.field_name)) {
      return;
    }
    switch (fieldConfig.viewType) {
      case 'Cloudinary': {
        if (fieldConfig?.type === 'ImageCloudinary') {
          data[fieldName] = prepareImageCloudinary(content[fieldName], fieldConfig.multivalued);
        } else {
          data[fieldName] = content[fieldName] || null;
        }
        break;
      }
      case 'Entity': {
        const entityContentType = getContentTypeByConfigArg('type', fieldConfig.type, contentTypes);
        if (fieldConfig.multivalued) {
          data[fieldName] = content[fieldName] && content[fieldName].length ? content[fieldName].map(v => variablesInputSetup(entityContentType, contentTypes, v)) : [];
        } else {
          data[fieldName] = content[fieldName] ? variablesInputSetup(entityContentType, contentTypes, content[fieldName]) : null;
        }
      } break;
      case 'EntitySelect':
        // PUT EVERYTHING WIHTOUT OBJECT
        data[fieldName] = prepareObject(content[fieldName], fieldConfig.multivalued);
        break;
      case 'Picker':
        // PUT EVERYTHING WITHOUT OBJECT
        data[fieldName] = prepareObject(content[fieldName], fieldConfig.multivalued);
        break;
      case 'PickerExternal': {
        const entityContentType = getContentTypeByConfigArg('type', fieldConfig.type, contentTypes);
        if (fieldConfig.multivalued) {
          data[fieldName] = content[fieldName] && content[fieldName].length ? content[fieldName].map(v => variablesInputSetup(entityContentType, contentTypes, v)) : [];
        } else {
          data[fieldName] = content[fieldName] ? variablesInputSetup(entityContentType, contentTypes, content[fieldName]) : null;
        }
        break;
      }
      case 'Switch':
        data[fieldName] = !!content[fieldName];
        break;
      case 'Number':
        data[fieldName] = !Number.isNaN(parseInt(content[fieldName])) ? content[fieldName] : null;
        break;
      default:
        data[fieldName] = content.hasOwnProperty(fieldName) ? content[fieldName] : null;
        break;
    }

    switch (fieldConfig.viewTypeCustom) {
      case 'CSVTable': {
        const entityContentType = getContentTypeByConfigArg('type', fieldConfig.type, contentTypes);

        if (fieldConfig.multivalued) {
          data[fieldName] = content[fieldName] && content[fieldName].length ? content[fieldName].map(v => variablesInputSetup(entityContentType, contentTypes, v)) : [];
        } else {
          data[fieldName] = content[fieldName] ? variablesInputSetup(entityContentType, contentTypes, content[fieldName]) : null;
        }
        break;
      }
      case 'PriceTab': {
        const entityContentType = getContentTypeByConfigArg('type', fieldConfig.type, contentTypes);

        if (fieldConfig.multivalued) {
          data[fieldName] = content[fieldName] && content[fieldName].length ? content[fieldName].map(v => variablesInputSetup(entityContentType, contentTypes, v)) : [];
        } else {
          data[fieldName] = content[fieldName] ? variablesInputSetup(entityContentType, contentTypes, content[fieldName]) : null;
        }
        break;
      }
      case 'LLMTags': {
        if (fieldConfig.resolvedField) {
          delete data[fieldName];
        }
        break;
      }
      default:
        break;
    }
  });

  return data;
};

export {
  filterFields,
  buildContentQuery,
  buildContentMutation,
  paths,
  generateContentQueryMapping,
  createRelatedContentQueries,
  createRelatedContentQueriesFromMapping,
  remapContentToQueryFields,
  variablesInputSetup
};
